import React, { useState } from 'react';
import { ChevronDown, FolderEdit, Star, Pencil, Check, X } from 'lucide-react';
import { Category } from '@/types/category';
import { SOP } from '@/types/sop';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Input } from '../ui/input'; // Added Input
import { useToast } from '../ui/use-toast'; // Added useToast
import { getDepartmentColorClass } from '../../utils/departmentColors';
import CreatorCategoryView from './CreatorCategoryView';
import ImportedCategoryView from './ImportedCategoryView';

interface CollapsibleCategoryProps {
  category: Category;
  sops: SOP[];
  userId: string;
  onVisibilityChange: (isPublic: boolean) => void;
  onDeleteCategory: () => void;
  onMoveSOP: (sop: SOP) => void;
  onRemoveFavorite: (sop: SOP) => void;
  onUpdateCategoryName: (categoryId: string, newName: string) => Promise<void>; // Added prop
}

export const CollapsibleCategory: React.FC<CollapsibleCategoryProps> = ({
  category,
  sops,
  userId,
  onVisibilityChange,
  onDeleteCategory,
  onMoveSOP,
  onRemoveFavorite,
  onUpdateCategoryName, // Added prop
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // Added state
  const [editingCategoryName, setEditingCategoryName] = useState(category.name); // Added state
  const { toast } = useToast(); // Added toast

  const sopCount = `(${sops.length} ${sops.length === 1 ? 'SOP' : 'SOPs'})`;
  const canEdit = category.name !== 'My Favorites' && category.userId === userId && !category.originalOwner;

  const handleSaveName = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editingCategoryName.trim()) {
      toast({
        title: "Error",
        description: "Category name cannot be empty.",
        variant: "destructive",
      });
      return;
    }
    try {
      await onUpdateCategoryName(category.id, editingCategoryName.trim());
      setIsEditing(false);
      toast({
        title: "Success",
        description: "Category name updated.",
      });
    } catch (error) {
      console.error("Failed to update category name:", error);
      const errorMessage = error instanceof Error ? error.message : "An unknown error occurred.";
      toast({
        title: "Error",
        description: `Failed to update category name: ${errorMessage}`,
        variant: "destructive",
      });
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditingCategoryName(category.name); // Reset to original name
  };

  return (
    <Card
      className="mb-4 last:mb-0 bg-white dark:bg-gray-800/50 hover:border-green-500 dark:hover:border-green-400 transition-colors duration-200"
      // Allow click to open/close only if not editing
      onClick={() => !isEditing && setIsOpen(!isOpen)}
    >
      <Collapsible
        open={isOpen}
        onOpenChange={(openState) => !isEditing && setIsOpen(openState)} // Prevent toggle if editing
        className="p-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1"> {/* Reduced gap for edit icon */}
            <CollapsibleTrigger asChild>
              {/* Make trigger clickable only if not editing */}
              <div
                className={`flex items-center gap-2 transition-colors duration-200 ${!isEditing ? 'hover:text-green-600 dark:hover:text-green-400 cursor-pointer' : 'cursor-default'}`}
                onClick={(e) => { if (isEditing) e.stopPropagation(); }} // Prevent toggle if editing
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isOpen ? 'transform rotate-180' : ''
                  }`}
                />
                {!isEditing ? (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{category.name}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{sopCount}</span>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1 h-auto text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCategoryName(category.name);
                          setIsEditing(true);
                        }}
                        aria-label="Edit category name"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Input
                      value={editingCategoryName}
                      onChange={(e) => setEditingCategoryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveName(e as any); // Cast needed for event type
                        if (e.key === 'Escape') handleCancelEdit(e as any); // Cast needed for event type
                      }}
                      className="h-8 text-sm"
                      autoFocus
                    />
                    <Button variant="ghost" size="sm" onClick={handleSaveName} className="p-1 h-auto text-green-600 hover:text-green-700">
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleCancelEdit} className="p-1 h-auto text-red-600 hover:text-red-700">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CollapsibleTrigger>
          </div>

          {/* Category Controls */}
          {/* Hide controls if editing name to prevent overlap/confusion */}
          {!isEditing && (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {category.name !== 'My Favorites' && ( // This condition is already handled by canEdit for the edit icon
                (category.userId === userId && !category.originalOwner) ? (
                  <CreatorCategoryView
                    category={category}
                    userId={userId}
                    onVisibilityChange={onVisibilityChange}
                    onDeleteCategory={onDeleteCategory}
                  />
                ) : (
                  <ImportedCategoryView
                    category={category}
                    onDeleteCategory={onDeleteCategory}
                  />
                )
              )}
            </div>
          )}
        </div>

        <CollapsibleContent className="pt-4">
          {sops.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 pl-6">
              {sops.map(sop => (
                <div key={sop.id} className="relative flex flex-col py-3 px-4 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <a
                          href={sop.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-green-600 dark:hover:text-green-400"
                        >
                          {sop.title}
                        </a>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {sop.author1 && `By: ${sop.author1}`}
                          {sop.author1 && sop.author2 && ', '}
                          {sop.author2}
                        </span>
                        {sop.department.split(',').map((dept, index) => {
                          const trimmedDept = dept.trim();
                          return trimmedDept ? (
                            <span
                              key={`${trimmedDept}-${index}`}
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${getDepartmentColorClass(trimmedDept, index)}`}
                            >
                              {trimmedDept}
                            </span>
                          ) : null;
                        })}
                        {sop.tags?.map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300 ring-1 ring-inset ring-gray-500/10 dark:ring-gray-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveSOP(sop);
                        }}
                        className="text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                      >
                        <FolderEdit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveFavorite(sop);
                        }}
                        className="text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300"
                      >
                        <Star className="h-4 w-4 fill-current" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 border border-dashed rounded-md">
              No SOPs in this category yet.
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default CollapsibleCategory;